import React, { type ReactNode } from "react";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import type { Theme } from "src/theme";

const useStyles = makeStyles((theme: Theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: theme.palette.common.white,
  },
}));

interface LoadingBackdrop {
  /** Cover the whole app. Stays up until something hides it. */
  show: () => void;
  hide: () => void;
}

// A ref, so callers can reach it from an effect or a callback without adding a
// dependency that would change when the backdrop opens or closes.
const LoadingBackdropContext = React.createContext<
  React.MutableRefObject<LoadingBackdrop>
>({ current: { show: () => undefined, hide: () => undefined } });

export function useLoadingBackdrop() {
  return React.useContext(LoadingBackdropContext);
}

/**
 * A spinner that outlives the component asking for it.
 *
 * Setting `window.location.href` doesn't block, so the caller is free to close
 * its dialog and unmount while the browser is still fetching the external page
 * — which on a slow connection leaves the user staring at an unchanged screen
 * for seconds. Callers show this right before the redirect; it stays up until
 * the page goes away.
 */
export function LoadingBackdropProvider({ children }: { children: ReactNode }) {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  // `setOpen` is stable, so this handle is created once and never changes.
  const backdrop = React.useRef<LoadingBackdrop>({
    show: () => setOpen(true),
    hide: () => setOpen(false),
  });

  React.useEffect(() => {
    // A page restored from the back/forward cache keeps its React state, so
    // navigating back from the external site would return to a covered app.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setOpen(false);
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  return (
    <LoadingBackdropContext.Provider value={backdrop}>
      {children}
      <Backdrop className={classes.backdrop} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </LoadingBackdropContext.Provider>
  );
}

export default LoadingBackdropProvider;
